import { useContext, useEffect } from 'react'
import { useFormik } from 'formik'
import { useMaintenanceStore, INITIAL_STATE } from 'Store/Maintenance'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { MaintenanceListViewContext } from '../List/Context'
import { MaintenanceFormModalContext } from './Context'
import * as Yup from 'yup'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import moment from 'moment'

const VALIDATION_SCHEMA = Yup.object().shape({
  saleOrderId: Yup.string().min(1, 'กรุณาเลือกใบสั่งขาย').required('กรุณาเลือกใบสั่งขาย'),
  problemReportBy: Yup.string().min(1, '').required(''),
  problemReportAt: Yup.date().typeError('กรุณาเลือกเวลางาน').required('กรุณาเลือกเวลางาน'),
  issueCategory: Yup.string()
    .min(1, 'กรุณากรอกหมวดหมู่ของปัญหา')
    .required('กรุณากรอกหมวดหมู่ของปัญหา'),
  issueTitle: Yup.string().min(1, 'กรุณากรอกปัญหา').required('กรุณากรอกปัญหา'),
  machineTitle: Yup.string()
    .min(1, 'กรุณากรอกชนิดเครื่องจักร/ขนส่ง')
    .required('กรุณากรอกชนิดเครื่องจักร/ขนส่ง'),
  jobStatus: Yup.string().min(1, 'กรุณาเลือกสถานะของงาน').required('กรุณาเลือกสถานะของงาน'),
})

const ViewModel = () => {
  const { formType } = useContext(MaintenanceFormModalContext)
  const { isView, isOpenMaintenanceModal, toggleMaintenanceModal } = useContext(
    MaintenanceListViewContext
  )
  const { selectedId, setSelectedId, getAll, getOne, selected } = useMaintenanceStore(
    state => ({
      selected: state.selected,
      selectedId: state.selectedId,
      setSelectedId: state.setSelectedId,
      getAll: state.getAll,
      getOne: state.getOne,
    }),
    shallow
  )
  const { create, update } = useMaintenanceStore(state => ({
    create: state.create,
    update: state.edit,
  }))
  const { saleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
    }),
    shallow
  )

  const fetchData = async () => {
    if (isOpenMaintenanceModal) {
      if (formType === 'edit' && selectedId) {
        const { data, success } = await getOne(selectedId)
        if (!success) {
          toast.error(data)
          toggleMaintenanceModal(false)
          return
        } else {
          formik.setValues({
            ...data,
            problemReportAt: data.problemReportAt ? moment(data.problemReportAt).tz('Asia/Bangkok') : null,
            startPeriod: data.startPeriod ? moment(data.startPeriod).tz('Asia/Bangkok') : null,
            endPeriod: data.endPeriod ? moment(data.endPeriod).tz('Asia/Bangkok') : null,
            saleOrderId: data.project.saleOrder.id,
          })
        }
      }
    }
  }

  const submit = async (values: any) => {
    if (formType === 'add') {
      const body: { [key: string]: any } = {
        saleOrderId: values.saleOrderId,
        jobStatus: values.jobStatus,
        problemReportAt: moment(values.problemReportAt).toISOString(),
        problemReportBy: values.problemReportBy ?? '',
        startPeriod: values.startPeriod ? moment(values.startPeriod).toISOString() : '',
        endPeriod: values.endPeriod ? moment(values.endPeriod).toISOString() : '',
        remark: values.remark ?? '',
        issueTitle: values.issueTitle ?? '',
        issueCategory: values.issueCategory ?? '',
        machineTitle: values.machineTitle ?? '',
      }

      const { data, success } = await create(body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('เพิ่มรายการแจ้งซ่อมบำรุง / รายการลาหยุดพนักงานสำเร็จ')
        toggleMaintenanceModal(false)
        await getAll()
      }
    } else if (formType === 'edit') {
      const body: { [key: string]: any } = {}

      Object.keys(values).forEach(key => {
        if (
          key === 'id' ||
          key === 'createdAt' ||
          key === 'updatedAt' ||
          key === 'createdByUser' ||
          key === 'createdByUserId' ||
          key === 'projectId' ||
          key === 'project'
        ) {
          return
        } else if (key === 'problemReportAt' || key === 'startPeriod' || key === 'endPeriod') {
          if (!values[key]) {
            return
          } else if (moment(values[key]).toISOString() !== moment(selected[key]).toISOString()) {
            body[key] = moment(values[key]).toISOString()
          }
        } else if (selected[key] !== values[key]) {
          body[key] = values[key]
        }
      })

      if (Object.keys(body).length === 0) {
        toast.error('No change')
        return
      }

      const { data, success } = await update(selectedId, body)
      if (!success) {
        toast.error(data)
        return
      } else {
        toast.success('แก้ไขรายการแจ้งซ่อมบำรุง / รายการลาหยุดพนักงานสำเร็จ')
        toggleMaintenanceModal(false)
        await getAll()
      }

      toggleMaintenanceModal(false)
      await getAll()
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_STATE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      await submit(values)
      resetForm()
    },
  })

  const handleClose = () => {
    toggleMaintenanceModal(false)
    setSelectedId(undefined)
    formik.resetForm()
  }

  const isValidEndDate = (currentDate: any, selectedDate: any) => {
    if (moment.utc(formik.values.startPeriod).isValid()) {
      return currentDate.isSameOrAfter(moment.utc(formik.values.startPeriod).subtract(1, 'day'))
    }

    return currentDate.isSameOrBefore(selectedDate)
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, formType, isOpenMaintenanceModal])

  useEffect(() => {
    return () => {
      formik.resetForm()
      setSelectedId(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isView,
    formType,
    isOpenMaintenanceModal,
    modalsRoot,
    handleClose,
    formik,
    saleOrder: saleOrder,
    isValidEndDate,
  }
}

export default ViewModel
